/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Divider, Typography } from '@mui/material';
import { Login, Logout } from '@mui/icons-material';
import moment from 'moment';

import Header from '@components/Header/Header';
import FicharHeader from '@components/HeaderFichaje/header';
import EsfirusButton from '@components/ui/Button/EsfirusButton';
import ModalGpsRequired from './BBs/ModalGpsRequired/ModalGpsRequired';
import ModalFichajeData from './BBs/ModalFichajeData/ModalFichajeData';
import { FichajeFormData } from './types';

import { fichajesService } from '@services/fichajes';
import { configurationService } from '@services/configApp';
import { dateFormat } from '@helpers/utils';
import { emptyResourceRedirect } from '@helpers/navigate';
import {
  TIPOS_FICHAJES,
  getFichajeType,
  getPosition,
  registerFichaje,
  timeFormatted,
} from './utils/functions';
import {
  isAdmin,
  isSupervisor,
  isEmpleado,
  isJefeObra,
} from '@services/hooks/UserHook';

import './TrackHoras.scss';

const INIT_TIME_DIFF = { h: '00', m: '00', s: '00' };

export default function TrackHoras() {
  const navigate = useNavigate();

  const empresas = useSelector((state) => (state as any).empresas).list;
  const { gpsIsRequired, hideRegister, hideMonthlySummary, showTimeKeepingTypeInput, showTimeKeepingDescriptionInput } =
    configurationService.getConfigFichaje();

  // Estados de control de entrada/salida
  const [lastFichaje, setLastFichaje] = useState<any>(null);
  const [errorLastFichaje, setErrorLastFichaje] = useState<string>("");
  const [fichado, setFichado] = useState<boolean>(false);
  const [showRegister, setShowRegister] = useState<boolean>(false);
  const [showMonthlySummary, setShowMonthlySummary] = useState<boolean>(false);

  // Estados para contador de tiempo
  const [initTime, setInitTime] = useState<any>(null);
  const [currentTime, setCurrentTime] = useState<any>(null);
  const [timeDiff, setTimeDiff] = useState(INIT_TIME_DIFF);

  // Modal activators
  const [showModalGpsRequired, setShowModalGpsRequired] = useState(false);
  const [showModalFichajeData, setShowModalFichajeData] = useState(false);
  const renderModalFichajeData = showTimeKeepingTypeInput || showTimeKeepingDescriptionInput

  // Loaders
  const [isLoadingLastFichaje, setIsLoadingLastFichaje] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const secondsCounter = useRef(null as any);

  // Carga del último fichaje
  useEffect(() => {
    loadLastFichaje();
    emptyResourceRedirect({
      check: empresas.length === 0,
      path: '/time-tracking',
      navigate,
    });
  }, []);

  // Actualizamos datos cuando cambie último fichaje
  useEffect(() => {
    if (!lastFichaje) {
      setFichado(false);
    } else {
      const lastFichajeType = getFichajeType(lastFichaje);
      const _fichado = lastFichajeType === TIPOS_FICHAJES.ENTRADA;
      setFichado(_fichado);
    }
  }, [lastFichaje]);

  // Seteamos hora inicial y contador dependiendo de si está fichado o no
  useEffect(() => {
    if (fichado) {
      secondsCounter.current = setInterval(
        () => setCurrentTime(moment()),
        1000
      );
      setInitTime(fichado ? moment(lastFichaje.dateIn) : null);
      setCurrentTime(moment());
    } else {
      clearInterval(secondsCounter.current);
      setInitTime(null);
      setCurrentTime(null);
    }
  }, [fichado]);

  // Seteamos la diferencia entre initDate y currentDate formateado
  useEffect(() => {
    if (initTime && currentTime) {
      const diff = Math.abs(currentTime.valueOf() - initTime.valueOf());
      const diffH = Math.floor(diff / 1000 / 60 / 60);
      const diffM = Math.floor(diff / 1000 / 60) - diffH * 60;
      const diffS = Math.floor(diff / 1000) - diffM * 60 - diffH * 60 * 60;

      setTimeDiff({
        h: timeFormatted(diffH) as any,
        m: timeFormatted(diffM) as any,
        s: timeFormatted(diffS) as any,
      });
    } else {
      setTimeDiff(INIT_TIME_DIFF);
    }
  }, [initTime, currentTime]);

  useEffect(() => {
    const visibilityRules = {
      no: (): boolean => true,
      employees: (): boolean => !isEmpleado(),
      managers: (): boolean => !isJefeObra(),
      employees_and_managers: (): boolean => !(isEmpleado() || isJefeObra()),
      everyone: (): boolean => isAdmin(),
    };

    setShowRegister(
      visibilityRules[hideRegister as keyof typeof visibilityRules]()
    );
    setShowMonthlySummary(
      visibilityRules[hideMonthlySummary as keyof typeof visibilityRules]()
    );
  }, [
    hideRegister,
    hideMonthlySummary,
    isAdmin,
    isSupervisor,
    isEmpleado,
    isJefeObra,
  ]);

  const loadLastFichaje = async () => {
    setIsLoadingLastFichaje(true);

    try {
      const lastFichajeResp = await fichajesService.getLastFichaje();
      setLastFichaje(lastFichajeResp?.data?.data ?? null);
    } catch (error: any) {
      if (error?.response?.status === 409) setErrorLastFichaje("Ya tienes un fichaje en curso, espera unos segundos.");
      else setErrorLastFichaje("No se ha podido recuperar el estado anterior de tu fichaje. Contacta con un administrador.");
    } finally {
      setIsLoadingLastFichaje(false);
    }
  };

  const sendToRegister = async (data?: FichajeFormData) => {
    setIsRegistering(true);
    const time = moment();

    try {
      const gps = await getPosition();
      const gpsCoords = gps?.coords
        ? ({ lat: gps?.coords?.latitude, long: gps?.coords?.longitude } as any)
        : null;

      const newFichajeResp = await registerFichaje(time, gpsCoords, data);
      setLastFichaje(newFichajeResp?.data?.data ?? null);
    } catch (error: any) {
      if (error.source === 'getPosition') {
        if (gpsIsRequired) {
          setShowModalGpsRequired(true);
        } else {
          const gpsCoords = null;
          const newFichajeResp = await registerFichaje(time, gpsCoords, data);
          setLastFichaje(newFichajeResp?.data?.data ?? null);
        }
      }
    } finally {
      setIsRegistering(false);
    }
  };

  if (isLoadingLastFichaje) {
    return (
      <>
        <Header />
        <FicharHeader
          showRegister={showRegister}
          showMonthlySummary={showMonthlySummary}
        />
        <div className="new-parte-container">
          <div className="new-parte-title text-center">
            <h4>Cargando último fichaje</h4>
          </div>
        </div>
      </>
    );
  }

  if (errorLastFichaje !== "") {
    return (
      <>
        <Header />
        <FicharHeader
          showRegister={showRegister}
          showMonthlySummary={showMonthlySummary}
        />
        <div className="new-parte-container">
          <div className="new-parte-title text-center">
            <h4>{errorLastFichaje}</h4>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <FicharHeader
        showRegister={showRegister}
        showMonthlySummary={showMonthlySummary}
      />

      <div className="new-parte-container">
        {/* Encabezado */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography variant="h5" fontSize={28} fontWeight="bold" color="primary">
            Control de Horas
          </Typography>
        </Box>

        {/* Último fichaje */}
        {lastFichaje && (
          <Box className="last-fichaje"
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1, alignSelf: "center" }}
            >
              Último fichaje
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ textAlign: "center" }}>
                <Login color="success" />
                <Typography variant="body2" fontWeight="bold">
                  {dateFormat(lastFichaje.dateIn)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Logout color="error" />
                <Typography variant="body2" fontWeight="bold">
                  {lastFichaje.dateOut ? dateFormat(lastFichaje.dateOut) : "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Reloj */}
        <Box textAlign="center">
          <Typography
            variant="h3"
            fontFamily="monospace"
            fontWeight="bold"
            color="primary"
            sx={{ opacity: 0.9 }}
          >
            {timeDiff.h}:{timeDiff.m}:{timeDiff.s}
          </Typography>
        </Box>

        {/* Botón de entrada/salida */}
        <Box textAlign="center">
          <EsfirusButton
            color={fichado ? "error" : "success"}
            startIcon={fichado ? <Logout /> : <Login />}
            disabled={isRegistering}
            onClick={async () => {
              if (fichado && renderModalFichajeData) {
                setShowModalFichajeData(true);
                return;
              }

              await sendToRegister();
            }}
            sx={{ px: 3, py: 1 }}
          >
            {fichado ? "Marcar Salida" : "Marcar Entrada"}
          </EsfirusButton>
        </Box>
      </div>

      {/* Modal de información GPS */}
      {showModalGpsRequired && (
        <ModalGpsRequired
          open={showModalGpsRequired}
          setOpen={setShowModalGpsRequired}
        />
      )}

      {/* Modal de solicitud de datos de fichaje */}
      {showModalFichajeData && (
        <ModalFichajeData
          open={showModalFichajeData}
          setOpen={setShowModalFichajeData}
          onSave={(data) => sendToRegister(data)}
        />
      )}
    </>
  );
}
